import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

// ─────────────────────────────────────────────────────────────────────────────
//  SCORING
// ─────────────────────────────────────────────────────────────────────────────
function scoreProduct(product, query) {
  const q         = query.toLowerCase().trim();
  const words     = q.split(/\s+/).filter(Boolean);
  const nameLower = product.name.toLowerCase();
  const catLower  = (product.category    || '').toLowerCase();
  const subLower  = (product.subCategory || '').toLowerCase();
  const brandLower= (product.brand       || '').toLowerCase();
  const allTags   = product.tags.map(t => t.toLowerCase());

  if (nameLower === q)                                                          return 100;
  if (nameLower.startsWith(q))                                                 return 92;
  if (nameLower.includes(q))                                                   return 85;
  if (words.length > 1 && words.every(w => nameLower.includes(w)))            return 80;
  if (catLower === q || subLower === q || brandLower === q)                    return 72;
  if (allTags.some(t => t === q))                                              return 68;
  if (catLower.includes(q) || subLower.includes(q) || brandLower.includes(q)) return 60;
  if (allTags.some(t => t.includes(q)))                                        return 55;
  if (words.some(w =>
    nameLower.includes(w) || catLower.includes(w) ||
    brandLower.includes(w) || allTags.some(t => t.includes(w))))               return 40;

  return 0;
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAP a Firestore product doc → search index entry
// ─────────────────────────────────────────────────────────────────────────────
function toSearchEntry(id, data) {
  const name        = data.name        || '';
  const category    = data.category    || '';
  const subCategory = data.subcategory || '';   // note: Firestore field is lowercase 'subcategory'
  const brand       = data.brand       || '';

  // Auto-generate tags from known fields so fuzzy matching still works
  const tags = [
    ...name.toLowerCase().split(/\s+/),
    category.toLowerCase(),
    subCategory.toLowerCase(),
    brand.toLowerCase(),
    ...(data.colors || []).map(c => c.toLowerCase()),
    data.isColored ? 'colored' : 'white',
  ].filter(Boolean);

  return {
    id,
    name,
    category,
    subCategory,
    brand,
    price:    data.startingPrice ?? data.price ?? 0,
    imageUrl: data.imageUrl || '',
    path:     '/product-details',
    state:    {
      product: {
        id,
        name,
        price: data.startingPrice ?? data.price ?? 0,
        category,
        categoryId: data.categoryId || '',
        subcategory: subCategory,
        subcategoryId: data.subcategoryId || '',
        brand,
        isColored: data.isColored ?? false,
        imageUrl: data.imageUrl || '',
        images: Array.isArray(data.images) ? data.images : [],
      },
    },
    tags,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
//  HOOK
// ─────────────────────────────────────────────────────────────────────────────
export function useSearch() {
  const [query,    setQuery]    = useState('');
  const [catalogue, setCatalogue] = useState([]);

  // Live-sync the products collection (only active products)
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'products'),
      (snap) => {
        const entries = snap.docs
          .filter(doc => (doc.data().status ?? 'active') === 'active')
          .map(doc => toSearchEntry(doc.id, doc.data()));
        setCatalogue(entries);
      },
      (err) => {
        console.error('[useSearch] Firestore error:', err);
      }
    );
    return () => unsub();
  }, []);

  const results = useMemo(() => {
    const q = query.trim();
    if (q.length < 2) return [];

    return catalogue
      .map(product => ({ product, score: scoreProduct(product, q) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map(({ product }) => product);
  }, [query, catalogue]);

  const clearSearch = useCallback(() => setQuery(''), []);

  return { query, setQuery, results, clearSearch };
}